import { RulePage } from '@publicodes/react-ui'
import Engine from 'publicodes'
import { ComponentProps, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { styled } from 'styled-components'

import {
	BodyStyle,
	H1Style,
	H2Style,
	H3Style,
	H4Style,
	H5Style,
	Link,
	ListProps,
	Markdown,
	StyledLinkProps,
	StyledLinkStyle,
	UlStyle,
} from '@/design-system'
import { DottedName } from '@/domaine/publicodes/DottedName'
import { NomModèle } from '@/domaine/PublicodesSimulationConfig'
import { useNavigation } from '@/lib/navigation'
import { parseLangue } from '@/locales/langue'
import { useSitePaths } from '@/sitePaths'

import DocumentationAccordion from './DocumentationAccordion'
import { RéférencesPublicodes } from './ReferencesPublicodes'

type PublicodesReferencesProps = {
	references?: Record<string, string>
	dottedName?: string
}

const apiEvaluateUrl: Record<NomModèle, string> = {
	'modele-as': 'modeles/as/evaluate',
	'modele-social': 'evaluate',
	'modele-ti': 'modeles/ti/evaluate',
}

export default function DocumentationPageBody({
	documentationPath,
	engine,
	nomModèle,
}: {
	documentationPath: string
	engine: Engine<DottedName>
	nomModèle: NomModèle
}) {
	const { absoluteSitePaths } = useSitePaths()
	const { i18n } = useTranslation()
	const { currentPath } = useNavigation()
	const rulePath = decodeURI(currentPath)
		.replace(documentationPath, '')
		.replace(/^\//, '')

	const RéférencesWithEngine = ({
		references,
		dottedName,
	}: PublicodesReferencesProps) =>
		RéférencesPublicodes({
			engine,
			références: references,
			dottedName: dottedName as DottedName,
		})

	const { current: renderers } = useRef({
		Head: Helmet,
		Link,
		Text: Markdown,
		References: RéférencesWithEngine,
		Accordion: DocumentationAccordion,
	} as ComponentProps<typeof RulePage>['renderers'])

	return (
		<StyledDocumentation>
			<RulePage
				language={parseLangue(i18n.language)}
				rulePath={rulePath}
				engine={engine}
				documentationPath={documentationPath}
				renderers={renderers}
				apiDocumentationUrl={absoluteSitePaths.développeur.api}
				apiEvaluateUrl={`https://mon-entreprise.urssaf.fr/api/v1/${apiEvaluateUrl[nomModèle]}`}
				npmPackage={nomModèle}
				mobileMenuPortalId="mobile-menu-portal-id"
			/>
		</StyledDocumentation>
	)
}

const StyledDocumentation = styled.div<ListProps & StyledLinkProps>`
	h1 {
		${H1Style}
		margin-top: 1rem;
	}
	h2 {
		${H2Style}
	}
	h3 {
		${H3Style}
	}
	h4 {
		${H4Style}
	}
	h5 {
		${H5Style}
	}
	p {
		${BodyStyle}
	}
	ul {
		${UlStyle}
	}
	a {
		${StyledLinkStyle}
	}

	font-family: ${({ theme }) => theme.fonts.main};

	.publicodes_btn-small {
		font-size: 0.8rem;
		background-color: ${({ theme }) => theme.colors.extended.grey[200]};
		border-radius: ${({ theme }) => theme.box.borderRadius};
		border: none;
		padding: ${({ theme }) => `${theme.spacings.xxs} ${theme.spacings.xs}`};
		color: ${({ theme }) => theme.colors.extended.grey[800]};

		&:hover {
			background-color: ${({ theme }) => theme.colors.extended.grey[300]};
		}
	}
	#documentation-rule-root nav ul li span button,
	#documentation-rule-root nav ul li.active .content {
		background-color: hsl(0deg 0% 90% / 50%);
	}
	#documentation-rule-root .node-value-pointer,
	#documentation-rule-root pre {
		background-color: ${({ theme }) =>
			theme.darkMode && theme.colors.extended.dark[600]};
	}
`
